import { FC, useContext, useState, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  SegmentedControl,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { TeamLogo } from "../../../../Components/TeamLogo";
import { TeamName } from "../../../../Components/TeamName";
import { CompetitionContext } from "../../../../CompetitionContext";
import { useLeaderboard } from "../../../../hooks/useLeaderboard";
import { useCompetition } from "../../../../hooks/useCompetition";
import { getPresentationParamsForOrigin } from "../../../../getPresentationParamsForOrigin";
import { IconTrophy } from "@tabler/icons-react";
import { CenteredLoader } from "../../../../Components/CenteredLoader";
import { CardContainer } from "../../../../Components/CardContainer";
import { WidgetHeader } from "../../../../Components/WidgetHeader";
import { ListRow } from "../../../../Components/ListRow";
import { EmptyState } from "../../../../Components/EmptyState";

interface GroupInfo {
  id: string;
  name: string;
  isResolved: boolean;
}

interface PageInfo {
  key: string;
  title: string;
  groups: GroupInfo[];
}

const MiniLeaderboardTable: FC<{
  competitionId: string;
  groupId: string;
  maxTeams: number;
}> = ({ competitionId, groupId, maxTeams }) => {
  const theme = useMantineTheme();

  const leaderboardData = useLeaderboard(competitionId, groupId);

  if (!leaderboardData.isSuccess) {
    return <CenteredLoader />;
  }

  const topTeams = leaderboardData.data.leaderboard.slice(0, maxTeams);

  if (topTeams.length === 0) {
    return <EmptyState message="Noch keine Mannschaften" />;
  }

  return (
    <Box>
      {/* Header Row */}
      <Flex align="center" py={4} px="xs" gap="sm">
        <Text size="xs" c="dimmed" w={24} ta="center">
          #
        </Text>
        <Box w={24} />
        <Text size="xs" c="dimmed" style={{ flex: 1 }}>
          Team
        </Text>
        <Text size="xs" c="dimmed" w={24} ta="center">
          S
        </Text>
        <Text size="xs" c="dimmed" w={32} ta="center">
          TD
        </Text>
        <Text size="xs" c="dimmed" w={28} ta="right">
          P
        </Text>
      </Flex>

      {topTeams.map((team, index) => {
        const isFirst = index === 0;
        const isLast = index === topTeams.length - 1;
        const highlightColor =
          theme.colors.fwcBlue?.[0] ?? theme.colors.blue[0];

        return (
          <ListRow
            key={team.teamId}
            isFirst={isFirst}
            isLast={isLast}
            highlighted={isFirst}
            highlightColor={highlightColor}
            to={`/teams/${team.teamId}`}
            gap="sm"
          >
            {/* Position */}
            <Text
              fw={700}
              size="sm"
              w={24}
              ta="center"
              c={isFirst ? "fwcBlue.7" : "dimmed"}
            >
              {team.position}
            </Text>

            {/* Team Logo */}
            <Box w={24}>
              <TeamLogo teamId={team.teamId} height={24} />
            </Box>

            {/* Team Name */}
            <Text
              size="sm"
              fw={isFirst ? 600 : 400}
              style={{ flex: 1 }}
              lineClamp={1}
            >
              <TeamName teamId={team.teamId} variant="medium" />
            </Text>

            {/* Games */}
            <Text size="sm" c="dimmed" w={24} ta="center">
              {team.games}
            </Text>

            {/* Goal Difference */}
            <Text
              size="sm"
              w={32}
              ta="center"
              c={
                (team.goalDifference ?? 0) > 0
                  ? "green"
                  : (team.goalDifference ?? 0) < 0
                    ? "red"
                    : "dimmed"
              }
              style={{ fontFamily: "Chivo Mono" }}
            >
              {(team.goalDifference ?? 0) > 0 ? "+" : ""}
              {team.goalDifference ?? 0}
            </Text>

            {/* Points */}
            <Text
              fw={700}
              size="sm"
              c={isFirst ? "fwcBlue.7" : undefined}
              w={28}
              ta="right"
            >
              {team.points}
            </Text>
          </ListRow>
        );
      })}
    </Box>
  );
};

export const MiniLeaderboard: FC<{ maxTeams?: number }> = ({
  maxTeams = 5,
}) => {
  const competitionId = useContext(CompetitionContext);
  const theme = useMantineTheme();
  const competition = useCompetition(competitionId);

  const leaderboardPages = getPresentationParamsForOrigin().leaderboardPages;

  // Build page info with group details
  const pagesWithGroups: PageInfo[] = useMemo(() => {
    if (!competition.isSuccess) return [];

    return leaderboardPages.map((page) => ({
      key: page.key,
      title: page.title,
      groups: page.groups.map((groupId) => {
        const group = competition.data.groups.find((g) => g.id === groupId);
        const hasTeams = group ? group.teamIds.length > 0 : false;
        return {
          id: groupId,
          name: group?.name ?? "Gruppe",
          isResolved: hasTeams,
        };
      }),
    }));
  }, [competition.isSuccess, competition.data, leaderboardPages]);

  // Find first page with resolved groups
  const firstResolvedPageIndex = pagesWithGroups.findIndex((page) =>
    page.groups.some((g) => g.isResolved),
  );
  const defaultPageIndex =
    firstResolvedPageIndex >= 0 ? firstResolvedPageIndex : 0;

  const [selectedPageIndex, setSelectedPageIndex] = useState(defaultPageIndex);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);

  // Reset group index when page changes
  const handlePageChange = (value: string) => {
    const newIndex = pagesWithGroups.findIndex((p) => p.key === value);
    if (newIndex >= 0) {
      setSelectedPageIndex(newIndex);
      setSelectedGroupIndex(0);
    }
  };

  if (
    !competitionId ||
    !competition.isSuccess ||
    pagesWithGroups.length === 0
  ) {
    return null;
  }

  const currentPage = pagesWithGroups[selectedPageIndex];
  if (!currentPage) return null;

  const currentGroup = currentPage.groups[selectedGroupIndex];
  if (!currentGroup) return null;

  const hasMultiplePages = pagesWithGroups.length > 1;
  const hasMultipleGroups = currentPage.groups.length > 1;

  return (
    <CardContainer>
      <WidgetHeader
        icon={
          <IconTrophy
            size={20}
            color={theme.colors.fwcBlue?.[6] ?? theme.colors.blue[6]}
          />
        }
        title="Tabelle"
        linkText="Ganze Tabelle"
        linkTo={`/leaderboard/${currentPage.key}`}
      />

      {/* Page Tabs (Vorrunde / Zwischenrunde) */}
      {hasMultiplePages && (
        <SegmentedControl
          value={currentPage.key}
          onChange={handlePageChange}
          data={pagesWithGroups.map((page) => ({
            label: page.title,
            value: page.key,
          }))}
          size="xs"
          fullWidth
          mb="sm"
        />
      )}

      {/* Group Selection */}
      {hasMultipleGroups && (
        <Flex gap={4} mb="sm" wrap="wrap">
          {currentPage.groups.map((group, index) => (
            <Button
              key={group.id}
              size="xs"
              variant={selectedGroupIndex === index ? "filled" : "light"}
              color={group.isResolved ? undefined : "gray"}
              onClick={() => {
                setSelectedGroupIndex(index);
              }}
              style={{
                flex: 1,
                minWidth: 60,
                opacity: group.isResolved ? 1 : 0.6,
              }}
            >
              {group.name}
            </Button>
          ))}
        </Flex>
      )}

      {/* Leaderboard Content */}
      {currentGroup.isResolved ? (
        <MiniLeaderboardTable
          competitionId={competitionId}
          groupId={currentGroup.id}
          maxTeams={maxTeams}
        />
      ) : (
        <EmptyState message="Noch nicht ausgespielt" />
      )}
    </CardContainer>
  );
};
